import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Form } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";
import { GlobalContext } from "../../../context/GlobalContext";
import axios from "../../../api/axios";
import helpers from "../../../utils/helpers";

const DISPOSITION_OPTIONS = ["Selling", "Trading in", "Keeping"];

/**
 * PD-6 Step 4 "Equipment".
 *
 * Merges EquipmentYear.js, EquipmentMake.js, EquipmentModel.js and
 * EquipmentCost.js into one screen, and relocates the existing
 * `purchaseType` field (previously a <Form.Select> in DealSummary.js,
 * ~line 198-212) here as a 2-chip control. Reuses the exact same
 * GlobalContext key (`questions.business.purchaseType`) and the exact same
 * `purchase_type` POST field — no duplicate field, no new backend contract.
 *
 * The "what is it replacing" text input and "Selling / Trading in / Keeping"
 * chips revealed on "Replacing" post to the new `replacing_description` /
 * `replacing_disposition` PartialApplication/BusinessApplication fields —
 * stored, but (per the PD-6 ticket) not synced to Salesforce like
 * `purchase_type` is.
 *
 * Deposit is a plain dollar amount in this UI (no percent chips, no live
 * "Deposit / Financed / Est. monthly" strip — that estimate math left the
 * app along with the sidebar's estimated-payment panel and the Offers step).
 * The wire contract is unchanged though: `down_payment_percentage` is still
 * a required field for business-application completeness
 * (COMPLETE_BUSINESS_APPLICATION_REQUIRED_FIELDS in the backend's
 * application_requirements.py), so the percentage is derived from the
 * dollar amount and posted alongside it, same field, just computed instead
 * of chosen directly.
 */
const StepEquipment = forwardRef(({ goNext, goBack, isFirstStep, footNote }, ref) => {
  const { state, dispatch } = useContext(GlobalContext);
  const [showLoader, setShowLoader] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [serverError, setServerError] = useState("");

  const [year, setYear] = useState(state?.questions?.personal?.financingYear ?? "");
  const [make, setMake] = useState(state?.questions?.personal?.financingMake ?? "");
  const [model, setModel] = useState(state?.questions?.personal?.financingModel ?? "");
  // Falls back to requestedAmount when equipmentCost hasn't been set by
  // this step yet — a brand-new application already has requestedAmount
  // populated at creation time (the URL price if given, otherwise the
  // link's preset — see GetStarted.js / the backend's priority logic), so
  // this field should show that instead of appearing blank on first visit.
  // Price, year, make and model are only ever *pre-filled* from the link /
  // dealer listing — the borrower can always change them, and their own
  // answer is what gets saved (see return_application_response).
  const [cost, setCost] = useState(() => {
    const initialCost = state?.questions?.equipmentCost ?? state?.questions?.requestedAmount;
    return initialCost ? parseInt(initialCost)?.toLocaleString() : "";
  });
  // Purple Wave company links only (internal or third-party) — decided by
  // the backend from PURPLE_WAVE_COMPANY_ID.
  const isPurpleWave = Boolean(state?.isPurpleWaveAccount);
  // Shown only when the link didn't already carry an ICN (?icn=) — lets the
  // applicant supply their Purple Wave item number by hand (direct link,
  // resumed session, cold start). Keyed on where the ICN came from, not on
  // purpleWaveIcn itself, since typing here writes into purpleWaveIcn too.
  const [purpleWaveIcn, setPurpleWaveIcn] = useState(
    state?.questions?.purpleWaveIcn ?? ""
  );
  const showPurpleWaveIcnField = isPurpleWave && !state?.questions?.purpleWaveIcnFromLink;
  const [purchaseType, setPurchaseType] = useState(
    state?.questions?.business?.purchaseType ?? ""
  );
  const [replacingDescription, setReplacingDescription] = useState(
    state?.questions?.business?.replacingDescription ?? ""
  );
  const [replacingDisposition, setReplacingDisposition] = useState(
    state?.questions?.business?.replacingDisposition ?? ""
  );

  // Deposit is a plain dollar amount, same field/format treatment as
  // Equipment price. Its saved value lives on `questions.personal.downPayment`
  // (a dollar figure already, unlike the legacy `questions.business.percentage`
  // this replaces).
  const [depositAmount, setDepositAmount] = useState(
    state?.questions?.personal?.downPayment
      ? parseInt(state?.questions?.personal?.downPayment)?.toLocaleString()
      : ""
  );

  const [errors, setErrors] = useState({});

  const handleCostChange = (e) => {
    const cleaned = e.target.value.replace(/,/g, "").replace(/[^0-9.]/g, "");
    const parsed = parseInt(cleaned);
    setCost(isNaN(parsed) ? "" : parsed.toLocaleString());
  };

  const handleDepositAmountChange = (e) => {
    const cleaned = e.target.value.replace(/,/g, "").replace(/[^0-9.]/g, "");
    const parsed = parseInt(cleaned);
    setDepositAmount(isNaN(parsed) ? "" : parsed.toLocaleString());
  };

  const priceValue = parseInt(String(cost).replace(/,/g, "")) || 0;
  const depositValue = parseInt(String(depositAmount).replace(/,/g, "")) || 0;
  // `down_payment_percentage` is still a required field for business-
  // application completeness on the backend (see the doc comment above) —
  // derived here so the dollar-only UI keeps posting it unchanged.
  const depositPercent = priceValue ? Math.min(Math.round((depositValue / priceValue) * 100), 100) : 0;

  // Mirrors the "Deposit chips move it instantly" behavior from the design
  // reference: the shell's sidebar reads equipmentCost/downPayment/the
  // financingYear-Make-Model/purchaseType fields from global state, so push
  // every change there live instead of waiting for Continue's dispatch —
  // same for the "Equipment"/"Purchase type" rows as for "Price". Local-only
  // (no network call) — the per-field partial-application autosave in
  // handleContinue below is unchanged. QUESTIONS fires first (it's the
  // wholesale replace — see handleContinue's own comment on this), then the
  // two narrower dispatches, so they can't clobber each other or get
  // clobbered by spreading a stale state.questions snapshot in the same
  // tick.
  useEffect(() => {
    dispatch({
      type: "QUESTIONS",
      payload: {
        ...state.questions,
        equipmentCost: priceValue || null,
        ...(showPurpleWaveIcnField && { purpleWaveIcn }),
      },
    });
    dispatch({
      type: "QUESTION_PERSONAL",
      payload: {
        ...state.questions.personal,
        downPayment: depositValue,
        financingYear: year,
        financingMake: make,
        financingModel: model,
      },
    });
    dispatch({
      type: "QUESTION_BUSINESS",
      payload: {
        ...state.questions.business,
        purchaseType,
        // "Just saved now": these two only apply when purchaseType is
        // "Replacement", same as handleContinue's own gating below — keeps
        // Back-then-Continue navigation from resurrecting stale replacement
        // details after the borrower has switched to "Adding to the fleet".
        replacingDescription: purchaseType === "Replacement" ? replacingDescription : "",
        replacingDisposition: purchaseType === "Replacement" ? replacingDisposition : "",
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    priceValue,
    depositValue,
    year,
    make,
    model,
    purchaseType,
    replacingDescription,
    replacingDisposition,
    purpleWaveIcn,
  ]);

  const validate = () => {
    const validationErrors = {};
    if (!cost) validationErrors.cost = "Please provide a valid amount.";
    const yearError = helpers.getEquipmentYearError(year);
    if (yearError) validationErrors.year = yearError;
    const makeError = helpers.getEquipmentTextError("Make", make);
    if (makeError) validationErrors.make = makeError;
    const modelError = helpers.getEquipmentTextError("Model", model);
    if (modelError) validationErrors.model = modelError;
    if (!purchaseType) {
      validationErrors.purchaseType = "Please select an option.";
    }
    if (depositValue > priceValue) {
      validationErrors.deposit = "Down payment cannot be greater than the equipment price.";
    }
    return validationErrors;
  };

  useImperativeHandle(ref, () => ({
    validateForSaveLater: () => {
      const validationErrors = validate();
      setErrors(validationErrors);
      setSubmitAttempted(true);
      return Object.keys(validationErrors).length === 0;
    },
  }));

  // Re-run validation live once the borrower has tried to submit once, so a
  // field's error clears as soon as it becomes valid instead of waiting for
  // another Continue press.
  useEffect(() => {
    if (submitAttempted) setErrors(validate());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitAttempted, cost, year, make, model, purchaseType, depositAmount]);

  const handleContinue = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    setServerError("");

    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const parsedCost = parseInt(String(cost).replace(/,/g, ""));
    const equipmentName = [year, make, (model || "").trim()].filter(Boolean).join(" ");

    // QUESTIONS must dispatch FIRST: its case does a wholesale
    // `{...state, questions: action.payload}` replace, and since its payload
    // is built from this render's `state.questions` closure (captured
    // before QUESTION_PERSONAL/QUESTION_BUSINESS below take effect), firing
    // it last would spread that stale `business`/`personal` snapshot back
    // over the top and silently revert purchaseType and the other fields
    // those two dispatches just set — same class of dispatch-ordering bug
    // fixed in StepReview.js's saveEquipment.
    dispatch({
      type: "QUESTIONS",
      payload: {
        ...state.questions,
        equipmentCost: isNaN(parsedCost) ? null : parsedCost,
        equipmentName: equipmentName || state?.questions?.equipmentName,
        ...(showPurpleWaveIcnField && { purpleWaveIcn }),
      },
    });
    dispatch({
      type: "QUESTION_PERSONAL",
      payload: {
        ...state.questions.personal,
        financingYear: year,
        financingMake: make,
        financingModel: model,
        isFinancingYearPrefilled: Boolean(year),
        isFinancingMakePrefilled: Boolean(make),
        isFinancingModelPrefilled: Boolean(model),
        downPayment: depositValue,
      },
    });
    dispatch({
      type: "QUESTION_BUSINESS",
      payload: {
        ...state.questions.business,
        purchaseType,
        percentage: depositPercent,
        replacingDescription: purchaseType === "Replacement" ? replacingDescription : "",
        replacingDisposition: purchaseType === "Replacement" ? replacingDisposition : "",
      },
    });

    const endpoint = `lender/partial-application/${state?.informationSlug}/?uuid=${state?.applicationUUID}`;

    setShowLoader(true);
    try {
      // Each field keeps its own autosave call, matching the payload shape
      // of its original standalone screen. These are independent fields, so
      // fire them concurrently instead of one round trip at a time.
      const requests = [
        axios.post(endpoint, { year }),
        axios.post(endpoint, { make }),
        axios.post(endpoint, { model }),
        axios.post(endpoint, { marketing_equipment_price: isNaN(parsedCost) ? null : parsedCost }),
        // Same field/contract as the old homeowner.js deposit radios —
        // percentage derived from the dollar amount the UI now collects.
        axios.post(endpoint, {
          down_payment_percentage: depositPercent,
          // The exact dollar figure too (the percentage is rounded), so a
          // resumed application shows the deposit actually entered.
          personal_down_payment: depositValue,
        }),
      ];
      if (showPurpleWaveIcnField && purpleWaveIcn) {
        requests.push(axios.post(endpoint, { purple_wave_icn: purpleWaveIcn }));
      }
      if (purchaseType) {
        // Same field/contract as the old DealSummary.js purchaseType select.
        requests.push(axios.post(endpoint, { purchase_type: purchaseType }));
      }
      if (purchaseType === "Replacement") {
        requests.push(
          axios.post(endpoint, {
            replacing_description: replacingDescription,
            replacing_disposition: replacingDisposition,
          })
        );
      }
      await Promise.all(requests);
      goNext();
    } catch (error) {
      setServerError(
        error?.response?.data?.message || "An error occurred. Please try again."
      );
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <div className="pd6-step">
      <h2 className="pd6-step__title">What you're financing</h2>
      <p className="pd6-step__helper">
        The asset itself, and whether it replaces something or adds capacity.
      </p>

      <Form noValidate onSubmit={handleContinue} className="pd6-form">
        <div className="pd6-field-row pd6-field-row--triple">
          <Form.Group className="pd6-field" controlId="stepEquipmentYear">
            <Form.Label>Year</Form.Label>
            <Form.Control
              type="text"
              placeholder="YYYY"
              autoComplete="off"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              isInvalid={submitAttempted && Boolean(errors.year)}
            />
            <Form.Control.Feedback type="invalid">{errors.year}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="pd6-field" controlId="stepEquipmentMake">
            <Form.Label>Make</Form.Label>
            <Form.Control
              type="text"
              placeholder="Make"
              autoComplete="off"
              value={make}
              onChange={(e) => setMake(e.target.value)}
              isInvalid={submitAttempted && Boolean(errors.make)}
            />
            <Form.Control.Feedback type="invalid">{errors.make}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="pd6-field" controlId="stepEquipmentModel">
            <Form.Label>Model</Form.Label>
            <Form.Control
              type="text"
              placeholder="Model"
              autoComplete="off"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              isInvalid={submitAttempted && Boolean(errors.model)}
            />
            <Form.Control.Feedback type="invalid">{errors.model}</Form.Control.Feedback>
          </Form.Group>
        </div>

        {showPurpleWaveIcnField && (
          <div className="pd6-field-row">
            <Form.Group className="pd6-field" controlId="stepEquipmentPurpleWaveIcn">
              <Form.Label>Purple Wave item number</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. EW4122"
                autoComplete="off"
                value={purpleWaveIcn}
                onChange={(e) => setPurpleWaveIcn(e.target.value)}
              />
            </Form.Group>
          </div>
        )}

        <div className="pd6-field-row">
          <Form.Group className="pd6-field" controlId="stepEquipmentCost">
            <Form.Label>
              Equipment price<span className="pd6-required">*</span>
            </Form.Label>
            <div className="pd6-currency-input">
              <span className="pd6-currency-input__prefix">$</span>
              <Form.Control
                type="text"
                placeholder="0"
                autoComplete="off"
                value={cost}
                onChange={handleCostChange}
                isInvalid={submitAttempted && Boolean(errors.cost)}
              />
            </div>
            <div className="pd6-field-helper">
              <span>Maximum Bid Amount</span>
            </div>
            <Form.Control.Feedback type="invalid" style={{ display: errors.cost ? "block" : "none" }}>
              {errors.cost}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="pd6-field" controlId="stepEquipmentDeposit">
            <Form.Label>Down Payment</Form.Label>
            <div className="pd6-currency-input">
              <span className="pd6-currency-input__prefix">$</span>
              <Form.Control
                type="text"
                placeholder="0"
                autoComplete="off"
                value={depositAmount}
                onChange={handleDepositAmountChange}
                isInvalid={submitAttempted && Boolean(errors.deposit)}
              />
            </div>
            <Form.Control.Feedback type="invalid" style={{ display: errors.deposit ? "block" : "none" }}>
              {errors.deposit}
            </Form.Control.Feedback>
          </Form.Group>
        </div>

        <Form.Group className="pd6-field" controlId="stepEquipmentPurchaseType">
          <Form.Label>
            Is this a replacement for a current one, or adding to your fleet?
            <span className="pd6-required">*</span>
          </Form.Label>
          <div className="pd6-chip-group">
            <button
              type="button"
              className={`pd6-chip ${purchaseType === "Replacement" ? "pd6-chip--selected" : ""}`}
              onClick={() => setPurchaseType("Replacement")}
            >
              Replacing existing equipment
            </button>
            <button
              type="button"
              className={`pd6-chip ${purchaseType === "Expansion" ? "pd6-chip--selected" : ""}`}
              onClick={() => setPurchaseType("Expansion")}
            >
              Adding to the fleet
            </button>
          </div>
          {submitAttempted && errors.purchaseType && (
            <div className="pd6-field-error">{errors.purchaseType}</div>
          )}

          {purchaseType === "Replacement" && (
            <div className="pd6-reveal-panel">
              <span className="pd6-reveal-panel__tag">Replacement details</span>
              <Form.Group className="pd6-field mt-2" controlId="stepEquipmentReplacingDescription">
                <Form.Label>What is it replacing?</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. 2015 Bobcat S650 skid steer"
                  autoComplete="off"
                  value={replacingDescription}
                  onChange={(e) => setReplacingDescription(e.target.value)}
                />
              </Form.Group>
              <Form.Label>What will happen to it?</Form.Label>
              <div className="pd6-chip-group">
                {DISPOSITION_OPTIONS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`pd6-chip ${replacingDisposition === option ? "pd6-chip--selected" : ""}`}
                    onClick={() => setReplacingDisposition(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          )}
        </Form.Group>

        {serverError && <div className="pd6-field-error mb-3">{serverError}</div>}

        {submitAttempted && Object.keys(errors).length > 0 && (
          <div className="pd6-form-summary-error">
            {Object.keys(errors).length} field{Object.keys(errors).length === 1 ? "" : "s"} still
            need{Object.keys(errors).length === 1 ? "s" : ""} an answer.
          </div>
        )}

        <div className="pd6-step__nav">
          <div className="pd6-step__nav-buttons">
            <button
              type="button"
              className="pd6-btn pd6-btn--ghost"
              onClick={goBack}
              disabled={showLoader || isFirstStep}
            >
              <span className="pd6-btn__arrow">←</span>
              <span>Back</span>
            </button>
            <button type="submit" className="pd6-btn pd6-btn--primary" disabled={showLoader}>
              {showLoader ? (
                <Spinner style={{ width: "1.2rem", height: "1.2rem" }} />
              ) : (
                <>
                  <span>Continue</span>
                  <span className="pd6-btn__arrow">→</span>
                </>
              )}
            </button>
          </div>
          {footNote && <span className="pd6-step__foot-note">{footNote}</span>}
        </div>
      </Form>
    </div>
  );
});

export default StepEquipment;
